import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { dataSourcesService } from '@/services/dataSourcesService';
import { adaptBackendDataSource, adaptBackendDataSourceGrouped } from '@/types/dataSources';
import type {
  DataSource,
  DataSourcesFilters,
  DataSourcesPaginatedResponse,
  GroupedDataSource,
  CreateDataSourceRequest,
  UpdateDataSourceRequest,
} from '@/types/dataSources';

// ============================================
// QUERY HOOKS
// ============================================

/**
 * Hook to fetch paginated list of datasources with filters
 * Use case: Tables, lists with pagination
 *
 * @param filters - Filter parameters (type, platform, is_active, search, pagination)
 * @param options - Query options (enabled, select, etc.)
 * @returns Query result with paginated datasources (flattened - 1 row per platform)
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useDataSources({
 *   type: 'competitor',
 *   account_id: 123,
 *   page: 1,
 *   per_page: 20
 * });
 * ```
 */
export const useDataSources = <TData = DataSourcesPaginatedResponse>(
  filters: DataSourcesFilters = {},
  options?: Omit<UseQueryOptions<DataSourcesPaginatedResponse, unknown, TData>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<DataSourcesPaginatedResponse, unknown, TData>({
    queryKey: ['datasources', 'list', filters],
    queryFn: () => dataSourcesService.getList(filters),
    enabled: options?.enabled !== false,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    ...options,
  });
};

/**
 * Hook to fetch a single datasource by ID
 * Returns array because backend groups by datasource (1 datasource = N platforms)
 *
 * @param datasourceId - DataSource ID
 * @param options - Query options (enabled, etc.)
 * @returns Query result with array of datasources (1 per platform)
 *
 * @example
 * ```tsx
 * const { data: datasources, isLoading } = useDataSource(456);
 * // datasources = [{ platform: 'instagram', ... }, { platform: 'twitter', ... }]
 * ```
 */
export const useDataSource = (
  datasourceId: number | null | undefined,
  options?: Omit<UseQueryOptions<DataSource[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<DataSource[]>({
    queryKey: ['datasource', datasourceId],
    queryFn: () => dataSourcesService.getDataSource(datasourceId!),
    enabled: options?.enabled !== false && !!datasourceId,
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    ...options,
  });
};

// ============================================
// MUTATION HOOKS
// ============================================

/**
 * Hook to create a new datasource with multiple platforms
 *
 * @returns Mutation for creating a datasource
 *
 * @example
 * ```tsx
 * const createMutation = useCreateDataSource();
 * createMutation.mutate({
 *   account_id: 123,
 *   name: 'Nike Competitor',
 *   data_source_type: 4, // competitor
 *   items: [
 *     {
 *       author_username: 'nike',
 *       social_network_id: 1, // instagram
 *       start_date: '2024-01-01',
 *       end_date: null,
 *       is_active: true
 *     },
 *     {
 *       author_username: 'nike',
 *       social_network_id: 2, // twitter
 *       start_date: '2024-01-01',
 *       is_active: true
 *     }
 *   ]
 * });
 * ```
 */
export const useCreateDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDataSourceRequest) => dataSourcesService.createDataSource(data),
    onSuccess: async (newDatasource, variables) => {
      // 1. Store the new datasource in its own cache
      queryClient.setQueryData(
        ['datasource', newDatasource.id],
        adaptBackendDataSource(newDatasource)
      );

      // 2. Update grouped queries cache and refetch
      const typeToQueryMap: Record<number, string> = {
        1: 'main_channel-grouped',
        2: 'influencers-grouped',
        3: 'partners-grouped',
        4: 'competitors-grouped',
      };
      const expectedQueryType = typeToQueryMap[newDatasource.data_source_type];
      const specificQueryKey = ['datasources', expectedQueryType, { account_id: newDatasource.account_id }];
      const currentData = queryClient.getQueryData<GroupedDataSource[]>(specificQueryKey);

      if (currentData) {
        // Optimistically update the cache
        const newGroupedDatasource = adaptBackendDataSourceGrouped(newDatasource);
        queryClient.setQueriesData(
          { queryKey: ['datasources', expectedQueryType], exact: false },
          (old: GroupedDataSource[] | undefined) => {
            if (!old) return old;
            return [newGroupedDatasource, ...old];
          }
        );

        // Refetch to ensure consistency with server
        await queryClient.refetchQueries({
          queryKey: specificQueryKey,
          exact: true,
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: ['datasources', expectedQueryType],
          refetchType: 'active',
        });
      }

      // 3. Update list queries (for tables using paginated lists)
      const datasourceType = ['main_channel', 'influencer', 'partner', 'competitor'][newDatasource.data_source_type - 1];
      const queryCaches = queryClient.getQueriesData<DataSourcesPaginatedResponse>({
        queryKey: ['datasources', 'list'],
      });

      queryCaches.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const filters = queryKey[2] as DataSourcesFilters | undefined;
        const shouldUpdate =
          (!filters?.account_id || filters.account_id === newDatasource.account_id) &&
          (!filters?.type || filters.type === datasourceType);

        if (shouldUpdate) {
          const newFlattenedDatasources = adaptBackendDataSource(newDatasource);
          queryClient.setQueryData(queryKey, {
            ...oldData,
            data: [...newFlattenedDatasources, ...oldData.data],
            meta: {
              ...oldData.meta,
              total: oldData.meta.total + newFlattenedDatasources.length,
            },
          });
        }
      });

      // 4. Refetch compact datasources (used by settings page)
      await queryClient.refetchQueries({
        queryKey: ['compact-datasources', newDatasource.account_id],
        exact: true,
      });

      // 5. Also refetch channel groups (used by V9 sidebar)
      await queryClient.refetchQueries({
        queryKey: ['channel-groups'],
        exact: false,
      });
    },
  });
};

/**
 * Hook to update a datasource (name, type, items, etc)
 *
 * WARNING: PUT /api/v2/datasources/{id} is DESTRUCTIVE!
 * Items not included in the items array will be DELETED.
 * Always GET the current datasource first to preserve existing items.
 *
 * @returns Mutation for updating a datasource
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateDataSource();
 * updateMutation.mutate({
 *   datasourceId: 123,
 *   data: {
 *     items: [
 *       { id: 456 }, // Keep this item
 *       { id: 789, dates: [{ start_date: '2024-02-01' }] }, // Update this item
 *     ]
 *   }
 * });
 * ```
 */
export const useUpdateDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ datasourceId, data }: {
      datasourceId: number;
      data: UpdateDataSourceRequest
    }) => dataSourcesService.updateDataSource(datasourceId, data),
    onSuccess: async (updatedDatasource, variables) => {
      // 1. Update the specific datasource query cache
      queryClient.setQueryData(
        ['datasource', variables.datasourceId],
        adaptBackendDataSource(updatedDatasource)
      );

      // 2. Update all matching list query caches with the updated datasource
      const queryCaches = queryClient.getQueriesData<DataSourcesPaginatedResponse>({
        queryKey: ['datasources', 'list'],
      });

      queryCaches.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // Check if this list contains items from this datasource
        const hasItems = oldData.data.some(
          (item) => item.datasource_id === updatedDatasource.id
        );

        if (hasItems) {
          // Remove old items and add updated ones
          const filteredData = oldData.data.filter(
            (item) => item.datasource_id !== updatedDatasource.id
          );
          const updatedFlattenedDatasources = adaptBackendDataSource(updatedDatasource);

          queryClient.setQueryData(queryKey, {
            ...oldData,
            data: [...updatedFlattenedDatasources, ...filteredData],
            // Total count might change if items were added/removed
            meta: {
              ...oldData.meta,
              total:
                oldData.meta.total -
                (oldData.data.length - filteredData.length) +
                updatedFlattenedDatasources.length,
            },
          });
        }
      });

      // 3. Update grouped queries cache
      const groupedCaches = queryClient.getQueriesData<GroupedDataSource[]>({
        queryKey: ['datasources'],
        predicate: (query) =>
          query.queryKey[1] === 'competitors-grouped' ||
          query.queryKey[1] === 'partners-grouped',
      });

      groupedCaches.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const index = oldData.findIndex((ds) => ds.id === updatedDatasource.id);

        if (index !== -1) {
          // Replace the old datasource with the updated one
          const updatedGroupedDatasource = adaptBackendDataSourceGrouped(updatedDatasource);
          const newData = [...oldData];
          newData[index] = updatedGroupedDatasource;
          queryClient.setQueryData(queryKey, newData);
        }
      });

      // 4. Refetch compact datasources (used by settings page)
      await queryClient.refetchQueries({
        queryKey: ['compact-datasources'],
        exact: false,
      });

      // 5. Also refetch channel groups (used by V9 sidebar)
      await queryClient.refetchQueries({
        queryKey: ['channel-groups'],
        exact: false,
      });
    },
  });
};

/**
 * Hook to delete a datasource item (removes 1 platform from a datasource)
 *
 * NOTE: API v2 doesn't have DELETE /api/v2/datasources/items/{id}
 * Implementation: GET datasource → filter item → PUT with remaining items
 *
 * @returns Mutation for deleting a datasource item
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteDataSourceItem();
 * deleteMutation.mutate({
 *   datasourceId: 123,
 *   itemId: 456
 * });
 * ```
 */
export const useDeleteDataSourceItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ datasourceId, itemId }: {
      datasourceId: number;
      itemId: number
    }) => dataSourcesService.deleteDataSourceItem(datasourceId, itemId),
    onSuccess: () => {
      // DON'T invalidate list queries to prevent unnecessary refetches
      // Deleted items will disappear when user refreshes or navigates

      // Only invalidate channel groups (used by V9 sidebar) which needs immediate updates
      queryClient.invalidateQueries({
        queryKey: ['channel-groups'],
        exact: false,
        refetchType: 'none',
      });
    },
  });
};

/**
 * Hook to delete an entire datasource (all platforms)
 *
 * @returns Mutation for deleting a datasource
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteDataSource();
 * deleteMutation.mutate(123); // Delete datasource ID 123 (all items/platforms)
 * ```
 */
export const useDeleteDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (datasourceId: number) => dataSourcesService.deleteDataSource(datasourceId),
    onSuccess: async (_, datasourceId) => {
      // 1. Remove from specific datasource cache
      queryClient.removeQueries({
        queryKey: ['datasource', datasourceId],
      });

      // 2. Remove from all list query caches
      const queryCaches = queryClient.getQueriesData<DataSourcesPaginatedResponse>({
        queryKey: ['datasources', 'list'],
      });

      queryCaches.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // Filter out all items that belong to this datasource
        const filteredData = oldData.data.filter(
          (item) => item.datasource_id !== datasourceId
        );

        if (filteredData.length !== oldData.data.length) {
          queryClient.setQueryData(queryKey, {
            ...oldData,
            data: filteredData,
            meta: {
              ...oldData.meta,
              total: oldData.meta.total - (oldData.data.length - filteredData.length),
            },
          });
        }
      });

      // 3. Remove from grouped queries cache
      const groupedCaches = queryClient.getQueriesData<GroupedDataSource[]>({
        queryKey: ['datasources'],
        predicate: (query) =>
          query.queryKey[1] === 'competitors-grouped' ||
          query.queryKey[1] === 'partners-grouped',
      });

      groupedCaches.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const filteredData = oldData.filter((ds) => ds.id !== datasourceId);

        if (filteredData.length !== oldData.length) {
          queryClient.setQueryData(queryKey, filteredData);
        }
      });

      // 4. Refetch compact datasources (used by settings page)
      await queryClient.refetchQueries({
        queryKey: ['compact-datasources'],
        exact: false,
      });

      // 5. Also refetch channel groups (used by V9 sidebar)
      await queryClient.refetchQueries({
        queryKey: ['channel-groups'],
        exact: false,
      });
    },
  });
};

// ============================================
// GROUPED QUERY HOOKS
// ============================================

/**
 * Hook to fetch grouped competitors (datasources with items grouped)
 * Returns GroupedDataSource[] instead of flattened DataSource[]
 */
export const useCompetitorsGrouped = (
  filters?: { account_id?: number; is_active?: boolean; search?: string },
  options?: Omit<UseQueryOptions<GroupedDataSource[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GroupedDataSource[]>({
    queryKey: ['datasources', 'competitors-grouped', filters],
    queryFn: async () => {
      const response = await dataSourcesService.getListGrouped({
        account_id: filters?.account_id,
        type: 'competitor',
        is_active: filters?.is_active,
        search: filters?.search,
      });
      return response.data;
    },
    enabled: options?.enabled !== false && !!filters?.account_id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};

/**
 * Hook to fetch grouped partners (datasources with items grouped)
 * Returns GroupedDataSource[] instead of flattened DataSource[]
 */
export const usePartnersGrouped = (
  filters?: { account_id?: number; is_active?: boolean; search?: string },
  options?: Omit<UseQueryOptions<GroupedDataSource[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery<GroupedDataSource[]>({
    queryKey: ['datasources', 'partners-grouped', filters],
    queryFn: async () => {
      const response = await dataSourcesService.getListGrouped({
        account_id: filters?.account_id,
        type: 'partner',
        is_active: filters?.is_active,
        search: filters?.search,
      });
      return response.data;
    },
    enabled: options?.enabled !== false && !!filters?.account_id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
    ...options,
  });
};
